import { Section, SectionParagraph, SectionTitle } from '@/components/shared/Content';
import { useSectionEventOnViewportEntrance } from '@/hooks/useSectionEventOnViewportEntrance';
import useLottie from '@/hooks/useLottie';
import { Flex } from '@lemonade-hq/cdk';
import parse from 'html-react-parser';
import React, { useContext, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import fsdGraphLottie from '@/components/car/assets/value/fsd-graph-lottie.json';
import { CarPageContext } from '@/components/car/CarPageContext';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';

const COLORS = {
  background: '#f7f7f7',
};

const StyledSection = styled(Section)`
  background-color: ${COLORS.background};
`;

const LottieContainer = styled(Flex)`
  width: 100%;
  max-width: 1200px;
  margin-top: 48px;
  margin-bottom: 24px;
  justify-content: center;
  position: relative;
  overflow: hidden;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 35vw;
    max-width: 600px;
    pointer-events: none;
    z-index: 1;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, ${COLORS.background} 0%, transparent 100%);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, ${COLORS.background} 0%, transparent 100%);
  }

  ${MEDIA_QUERIES.mobile`
  &::before {
    left: 0;
    background: linear-gradient(to right, ${COLORS.background} 0%, transparent 60%);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, ${COLORS.background} 0%, transparent 60%);
  }
  `}
`;

const LottieWrapper = styled(Flex)`
  width: 1400px;
  flex-shrink: 0;
`;

const BelowChartText = styled(SectionParagraph)`
  color: #4a4a4a;
  ${MEDIA_QUERIES.mobile`
     max-width: 335px;
  `}
`;

export interface ValuePropositionProps {
  title: string;
  subtitle: string;
  belowChartText?: string;
}

const ValueProposition: React.FC<ValuePropositionProps> = ({ title, subtitle, belowChartText }) => {
  const { analytics } = useContext(CarPageContext);
  const intersectionRef = useSectionEventOnViewportEntrance({
    sectionName: 'value_proposition',
    page: analytics?.page,
  });

  const lottieRef = useRef<HTMLDivElement>(null);
  const played = useRef(false);
  const { ref: inViewRef, inView } = useInView({
    threshold: 0.15,
    triggerOnce: true,
  });

  const lottieAnimation = useLottie(lottieRef, {
    animationData: fsdGraphLottie,
    lottieConfig: {
      loop: false,
    },
  });

  useEffect(() => {
    if (lottieAnimation == null) return;

    if (inView && !played.current) {
      played.current = true;
      lottieAnimation.play();
    }
  }, [lottieAnimation, inView]);

  return (
    <StyledSection pt={[60, 96]} pb={[60, 96]} ref={intersectionRef} width="100%">
      <Flex flexDirection="column" alignItems="center" maxWidth="800px" gap="12px" mb={[10, 0, 0]}>
        <SectionTitle tabIndex={0}>{parse(title)}</SectionTitle>
        <SectionParagraph tabIndex={0}>{parse(subtitle)}</SectionParagraph>
      </Flex>

      <LottieContainer ref={inViewRef}>
        <LottieWrapper ref={lottieRef} />
      </LottieContainer>
      {belowChartText && <BelowChartText>{parse(belowChartText)}</BelowChartText>}
    </StyledSection>
  );
};

export default React.memo(ValueProposition);
